import { Box, CardMedia, Typography } from '@mui/material';

import IdeaCardContent from './IdeaCardContent';
import type { Idea } from '../../interfaces/Idea';
import { resolveIdeaImageSrc } from '../../utils/ideaImage';
import { useCachedImage } from '../../hooks/useCachedImage';
import { getPublicAssetUrl } from '../../utils/publicAsset';

export interface VideoIdeaCardProps {
  showBackground: boolean;
  idea: Idea;
}

const VideoIdeaCard = ({ showBackground, idea }: VideoIdeaCardProps) => {
  const isProcessing = idea.processingStatus === 'processing';
  const hasError = idea.processingStatus === 'errored';
  const canShowThumbnail = !isProcessing && !hasError;
  const thumbnailSrc = canShowThumbnail ? resolveIdeaImageSrc(idea) : '';
  const { isLoading, handleLoad, handleError } = useCachedImage(thumbnailSrc);

  return (
    <IdeaCardContent showBackground={showBackground} showPadding={false}>
      <Box sx={{ position: 'relative', minHeight: 140, backgroundColor: '#111' }}>
        {canShowThumbnail && (
          <CardMedia
            component="img"
            image={thumbnailSrc}
            onLoad={handleLoad}
            onError={handleError}
            sx={{
              display: 'block',
              width: '100%',
              minHeight: 140,
              maxHeight: 240,
              objectFit: 'cover',
              opacity: isLoading ? 0 : 1,
            }}
          />
        )}
        {(isLoading || isProcessing) && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: 140, p: 2 }}
          >
            <CardMedia
              component="img"
              image={getPublicAssetUrl('/img/dotstorming-logo-compact.gif')}
              sx={{ width: 40, height: 40 }}
            />
          </Box>
        )}
        {hasError && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: 140, p: 2, color: '#fff', textAlign: 'center' }}
          >
            <Typography variant="body2" fontWeight={700}>
              Video processing failed
            </Typography>
          </Box>
        )}
        {!isProcessing && !hasError && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                width: 0,
                height: 0,
                borderTop: '14px solid transparent',
                borderBottom: '14px solid transparent',
                borderLeft: '22px solid rgba(255, 255, 255, 0.85)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          </Box>
        )}
      </Box>
      {idea.title && idea.title.trim().length > 0 && (
        <Typography
          variant="body2"
          sx={{ p: 1.5 }}
          dangerouslySetInnerHTML={{
            __html: idea.title,
          }}
        />
      )}
    </IdeaCardContent>
  );
};

export default VideoIdeaCard;
